// React
import { FunctionComponent, JSXElementConstructor, ReactElement, useCallback, useEffect, useState } from "react";
// Components
import { Page, PageConfiguration, ToastViewer } from "@fyrstain/fhir-front-library";
import { MenuItem } from "@fyrstain/fhir-front-library";
// Translation
import i18n from "i18next";
// Authentication
import UserService from "src/services/UserService";
// React router dom
// Fhir
import Client from "fhir-kit-client";
import { Bundle, Device } from "fhir/r5";
// Service
import { isLightMode, isRouteAllowedInLightMode } from "src/services/LightMode";
import { toPublicUrl } from "src/services/PublicUrl";
import { useEnvironment } from "src/context/EnvironmentContext";
import { deviceDisplayName } from "src/services/fhirR4CrossVersion";

const PandoraPage: FunctionComponent<{
    titleKey?: string;
    loading?: boolean;
    children?: ReactElement<any, string | JSXElementConstructor<any>> | undefined;
    needsLogin: boolean;
}> = (props) => {

    /////////////////////////////////
    //        NAVIGATION           //
    /////////////////////////////////

    const [validatorMenus, setValidatorMenus] = useState([] as MenuItem[]);

    const { currentEnvironment, configuration, switchEnvironment } = useEnvironment();
    const [environmentError, setEnvironmentError] = useState<string | null>(null);

    const handleLogin = useCallback((config: MenuItem) => {
        if (config.needsLogin) {
            if (!UserService.isAuthenticated()) {
                UserService.doLogin();
            }
        }
    }, []);

    useEffect(() => {
        const fhirClient = new Client({
            baseUrl: currentEnvironment?.validateurUrl ?? process.env.REACT_APP_FHIR_URL ?? 'fhir'
        });
        void getValidatorsMenus(fhirClient);
    }, [currentEnvironment]);

    type AppDropdownItem = {
        title?: string;
        link?: string;
        onClick?: (event?: React.MouseEvent<HTMLAnchorElement>) => void | Promise<void>;
        additionalItems?: AppDropdownItem[];
    };

    async function getValidatorsMenus(fhirClient: Client) {
        try {
            const response = await fhirClient.search({
                resourceType: "Device",
                searchParams: { "type": "http://fyrstain.com/pdt/ValueSet/systemTypes|validator" }
            });
            if (response.resourceType !== 'Bundle') {
                throw Error(response.statusText);
            }
            const bundle: Bundle = response as Bundle;
            setValidatorMenus(bundle.entry?.filter(entry => entry.resource?.resourceType === "Device")
                .map(entry => entry.resource)
                .map(resource => {
                    return {
                        title: deviceDisplayName(resource as Device),
                        link: toPublicUrl('/ValidatorHome/' + resource?.id),
                        needsLogin: false
                    } as MenuItem
                }) ?? []);
        } catch (error) {
            console.log(error);
        }
    }

    const handleLangChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        i18n.changeLanguage(event.target.value);
    };
    
    const handleEnvironmentSwitch = async (environmentId: string) => {
        setEnvironmentError(null);

        const success = await switchEnvironment(environmentId);

        if (!success) {
            setEnvironmentError("Unable to switch environment");
        }
    };

    const environmentDropdownItems = [
        {
            title: `Environment : ${currentEnvironment?.name ?? "Default"}`,
            link: "#",
            onClick: (event?: React.MouseEvent<HTMLAnchorElement>) => {
                event?.preventDefault();
            }
        },
        ...((configuration?.environments ?? []).map((env): AppDropdownItem => ({
            title: env.name,
            link: "/Home",
            onClick: async (event?: React.MouseEvent<HTMLAnchorElement>) => {
                event?.preventDefault();

                if (currentEnvironment?.id === env.id){
                    return;
                }

                await handleEnvironmentSwitch(env.id);
            }
        })))
    ];

    // Main menu
    const validatorsMenuItem: MenuItem = {
        title: i18n.t('navbar.items.validators'),
        subItems: validatorMenus
    };

    const testsMenuItem: MenuItem = {
        title: i18n.t('navbar.items.tests'),
        subItems: [
            {
                title: i18n.t('navbar.items.testscripts'),
                link: toPublicUrl('/TestScript'),
                needsLogin: true
            },
            {
                title: i18n.t('navbar.items.testplans'),
                link: toPublicUrl('/TestPlans'),
                needsLogin: true
            },
        ]
    };

    // Submenu
    const reportSubItems: MenuItem[] = [
        {
            title: i18n.t('navbar.items.testscripts'),
            link: toPublicUrl('/TestReports'),
            needsLogin: true
        },
        {
            title: i18n.t('navbar.items.testplans'),
            link: toPublicUrl('/TestPlan/Report'),
            needsLogin: true
        },
        {
            title: i18n.t('navbar.items.validation'),
            link: toPublicUrl('/ValidationReports'),
            needsLogin: true
        },
        {
            title: i18n.t('navbar.items.evaluation'),
            link: toPublicUrl('/EvaluationReports'),
            needsLogin: true
        },
    ];

    // Light submenu
    const filteredReportSubItems: MenuItem[] = isLightMode()
        ? reportSubItems.filter(item => item.link && isRouteAllowedInLightMode(item.link))
        : reportSubItems;

    const reportsMenuItem: MenuItem = {
        title: i18n.t('navbar.items.reports'),
        subItems: filteredReportSubItems
    };

    const messagesMenuItem: MenuItem = {
        title: i18n.t('navbar.items.messages'),
        link: toPublicUrl('/Exchanges'),
        needsLogin: true
    };

    const systemsMenuItem: MenuItem = {
        title: i18n.t('navbar.items.systems'),
        link: toPublicUrl('/Systems'),
        needsLogin: true
    };

    const auditMessagesMenuItem: MenuItem = {
        title: "MESSAGES",
        link: toPublicUrl("/AuditEventExchanges"),
        needsLogin: true
    };
    const finalMenuItems: MenuItem[] = isLightMode()
        ? [
            validatorsMenuItem,
            reportsMenuItem,
            auditMessagesMenuItem
        ]
        : [
            validatorsMenuItem,
            testsMenuItem,
            reportsMenuItem,
            messagesMenuItem,
            systemsMenuItem
        ];

    const fullConfig: PageConfiguration = {
        // Translation
        language: i18n.t,
        navigationBarConfigs: {
            homeLink: toPublicUrl('/Home'),
            logoLink: toPublicUrl('/assets/pandoraLogoHoriz.svg'),
            alt: 'Pandora Logo',
            authentication: {
                token: UserService.getKC().token,
                handleLogin: handleLogin,
                doLogin: UserService.doLogin,
                doLogout: UserService.doLogout,
                isAuthenticated: () => UserService.isAuthenticated(),
                getUserName: () => UserService.getUsername(),
            },
            
            menuItems: finalMenuItems,

            dropDownItems: [
                {
                    additionalItems: environmentDropdownItems
                }
            ],
        },
        titleKey: props.titleKey,
        loading: props.loading,
        children: <>
            <ToastViewer/>
                {environmentError && (
                    <div style={{ padding: "0.5rem 1rem", color: "red", fontSize: "0.9rem" }}>
                {environmentError}
                </div>
                )}
            { props.children }
            </>
        ,
        needsLogin: props.needsLogin,
        footerConfigs: {
            languages: {
                default: i18n.language,
                onChange: handleLangChange,
                options: [
                    {
                        label: "English",
                        value: "en"
                    },
                    {
                        label: "Français",
                        value: "fr"
                    }
                ]
            },
            logo: [
                {
                    src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                    alt: "Horizontal logo type",
                    href: toPublicUrl("/Home")
                },
                ...(process.env.REACT_APP_DISPLAY_CLIENT_LOGO === 'true' ?
                    [
                        {
                            src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                            alt: "Horizontal logo type",
                            href: toPublicUrl("/Home")
                        },
                        {
                            src: toPublicUrl("/assets/client_logo.jpg"),
                            alt: "HL7 Europe logo"
                        }
                    ]
                    : [])
            ],
            items: [
                {
                    label: i18n.t('footer.items.about'),
                    href: toPublicUrl("/InProgress")
                },
                {
                    label: i18n.t('footer.items.contact'),
                    href: toPublicUrl("/InProgress")
                },
                {
                    label: i18n.t('footer.items.problemtracking'),
                    href: toPublicUrl("/InProgress")
                },
                {
                    label: i18n.t("footer.items.implementationguide"),
                    href: toPublicUrl("/ImplementationGuide"),
                },
            ]
        }
    };

    return <Page {...fullConfig} />;
};

export default PandoraPage;
